import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background group gpu-accelerated focus-ring-enhanced disabled:pointer-events-none transition-all duration-300 ease-smooth',
  {
    variants: {
      variant: {
        default:
          'bg-ocean-500 text-white hover:bg-ocean-600 disabled:opacity-60 border-ocean-500 shadow-soft hover:shadow-medium micro-interaction',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-soft hover:shadow-medium micro-interaction hover:animate-wiggle',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-soft hover:shadow-medium micro-interaction',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-soft hover:shadow-medium micro-interaction',
        ghost: 'hover:bg-accent hover:text-accent-foreground micro-interaction',
        link: 'text-primary underline-offset-4 hover:underline transition-colors duration-200',
        gradient:
          'bg-gradient-to-r from-ocean-600 to-ocean-200 text-white hover:from-ocean-700 hover:to-ocean-500 disabled:opacity-100',
        'gradient-outline':
          'border-2 border-transparent bg-gradient-to-r from-sky-900 to-sky-400 bg-clip-padding text-white hover:opacity-90 shadow-glow hover:shadow-large hover-lift relative before:absolute before:inset-0 before:bg-gradient-to-r before:from-sky-900 before:to-sky-400 before:rounded-lg before:-z-10',
        success:
          'bg-green-600 text-white hover:bg-green-700 shadow-soft hover:shadow-medium micro-interaction',
        warning:
          'bg-yellow-600 text-white hover:bg-yellow-700 shadow-soft hover:shadow-medium micro-interaction',
        ocean:
          'bg-ocean-500 text-white hover:bg-ocean-600 border-ocean-500 shadow-soft hover:shadow-medium micro-interaction',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-lg px-8 text-base',
        xl: 'h-14 rounded-lg px-10 text-lg',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
        'icon-lg': 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
